import type {
  SetDateFilterOptions,
  DateFilterOption,
} from '@/views/Orders/Overview/FiltersSidePanel/Filters/DatesRadioGroupAndDatePickerField/DatesRadioGroupAndDatePickerField.types';
import type {OrderStatus, GetOrdersData, GetOrdersFilters} from '@myparcel-frontend/order-service';
import type {ValueOf} from '@myparcel-frontend/types';
import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import useAccount from '../../composables/useAccount/useAccount';
import useShop from '../../composables/useShop/useShop';
import useUser from '../../composables/useUser/useUser';
import {countValues} from '../../utils/countValues';
import {removeEmpty} from '../../utils/removeEmpty';
import {getDateFilter} from '../../views/Orders/Overview/FiltersSidePanel/Filters/DatesRadioGroupAndDatePickerField/dates.helper';

type CreateInitialQueryParamsOptions = {
  currentStatus?: OrderStatus[];
};

const createInitialQueryParams = (options?: CreateInitialQueryParamsOptions): NonNullable<GetOrdersData['query']> => ({
  query: '',
  filter: {
    shopId: [],
    status: options?.currentStatus ?? undefined,
    assignedUserId: [],
  },
  limit: 50,
});

export const useOrderFiltersStore = defineStore('orderFilters', () => {
  const allShopsFilter = ref<boolean>(false);
  const datesFilter = ref<ValueOf<typeof DateFilterOption>>();
  const periodFromFilter = ref('');
  const periodUntilFilter = ref('');
  const orderQueryParams = ref<NonNullable<GetOrdersData['query']>>(createInitialQueryParams());

  const {currentUser, shops} = useAccount();

  const setShopIdsFilter = (useAllShops: boolean) => {
    const shopIdValue: string[] = [];

    if (useAllShops) {
      shopIdValue.push(...shops.value.map((shop) => String(shop.id)));
    } else {
      const {currentShop} = useShop();
      shopIdValue.push(String(currentShop.value.id));
    }

    orderQueryParams.value.filter.shopId = shopIdValue;
  };

  watch(allShopsFilter, (newValue) => {
    setShopIdsFilter(newValue);
  });

  const updateOrderQueryFilter = ({
    type,
    payload,
  }: {
    type: keyof GetOrdersFilters;
    payload?: GetOrdersFilters[keyof GetOrdersFilters];
  }) => {
    if (payload === undefined || payload === 'false' || (Array.isArray(payload) && payload.length === 0)) {
      if (orderQueryParams.value.filter) {
        delete orderQueryParams.value.filter[type];
      }
      return;
    }

    orderQueryParams.value.filter[type] = payload;
  };

  const setAllShopsFilter = (newState: boolean) => {
    allShopsFilter.value = newState;
  };

  const setDatesFilter = (options: SetDateFilterOptions) => {
    datesFilter.value = options.newDate;

    periodFromFilter.value = options.newDate === 'period' ? options.from : '';
    periodUntilFilter.value = options.newDate === 'period' ? options.until : '';

    const newDateFilter = getDateFilter(options.newDate, options.from, options.until);

    updateOrderQueryFilter({
      type: 'orderedAt',
      payload: newDateFilter,
    });
  };

  const initOrderQueryParams = ({
    query,
    allShops,
    orderedAt,
    periodFrom,
    periodUntil,
  }: {
    query?: GetOrdersData['query'];
    allShops?: boolean;
    orderedAt?: ValueOf<typeof DateFilterOption>;
    periodFrom?: string;
    periodUntil?: string;
  }) => {
    if (query && Object.keys(query).length > 0) {
      orderQueryParams.value = {...orderQueryParams.value, ...query};
    }

    if (allShops !== undefined) {
      setAllShopsFilter(allShops);
      setShopIdsFilter(allShops);
    }

    if (orderedAt) {
      setDatesFilter({
        newDate: orderedAt,
        from: periodFrom || '',
        until: periodUntil || '',
      });
    }
  };

  const clearOrderFilters = () => {
    allShopsFilter.value = false;
    orderQueryParams.value = createInitialQueryParams({
      currentStatus: orderQueryParams.value.filter?.status,
    });
    datesFilter.value = undefined;
    periodFromFilter.value = '';
    periodUntilFilter.value = '';
    setShopIdsFilter(false);
  };

  /**
   * Handles assignment filter toggles without modifying unrelated filters.
   * Rules:
   * - 'me' (true): add current user id if not present.
   * - 'me' (false): remove current user id if present.
   * - 'others' (true): add all other user ids (excluding current) that have access in current shop scope.
   * - 'others' (false): remove all non-current user ids; keep current user id only if it was explicitly selected.
   * The resulting assignedUserId array is kept unique (via Set) and reflects combined toggle state.
   */
  const updateAssignUserIdFilter = (assignedTo: 'me' | 'others', newState: boolean) => {
    const currentUserId = String((currentUser.value as {id: string | number}).id);

    const assignMe = () => {
      const assigned = new Set(orderQueryParams.value.filter.assignedUserId);

      if (newState) {
        assigned.add(currentUserId);
      } else {
        assigned.delete(currentUserId);
      }

      orderQueryParams.value.filter.assignedUserId = Array.from(assigned);
    };

    const assignOthers = () => {
      const assigned = new Set(orderQueryParams.value.filter.assignedUserId);

      if (newState) {
        const {getUsersByShopIds} = useUser();

        const otherUserIds = getUsersByShopIds(orderQueryParams.value.filter.shopId)
          .map((user) => user.id.toString())
          .filter((id) => id !== currentUserId);

        otherUserIds.forEach((id) => assigned.add(id));

        orderQueryParams.value.filter.assignedUserId = Array.from(assigned);
        return;
      }

      // Turning off others: keep only current user if it was explicitly selected.
      orderQueryParams.value.filter.assignedUserId = assigned.has(currentUserId) ? [currentUserId] : [];
    };

    if (assignedTo === 'me') {
      assignMe();
    } else {
      assignOthers();
    }
  };

  const selectedFiltersAmount = computed(() => {
    const filters: NonNullable<GetOrdersData['query']>['filter'] = {
      ...orderQueryParams.value.filter,
    };
    delete filters.shopId;
    delete filters.status;

    if (filters.assignedUserId) {
      const currentUserId = String(currentUser.value.id);

      const filteredUserIds = filters.assignedUserId.filter(
        (userId) => userId === currentUserId || userId !== currentUserId,
      );

      // Create a tuple with a maximum of 2 values: currentUserId and one other userId if available
      filters.assignedUserId = filteredUserIds.includes(currentUserId)
        ? [currentUserId, ...filteredUserIds.filter((id) => id !== currentUserId).slice(0, 1)]
        : filteredUserIds.slice(0, 2);
    }

    if (filters.orderedAt && filters.orderedAt.length) {
      delete filters.orderedAt;
    }

    const remainingFilters = removeEmpty<NonNullable<GetOrdersData['query']>['filter']>(filters);
    let amount = countValues<NonNullable<GetOrdersData['query']>['filter']>(remainingFilters);

    if (datesFilter.value) {
      amount += 1;
    }

    if (allShopsFilter.value) {
      amount += 1;
    }

    return amount;
  });

  return {
    setAllShopsFilter,
    updateOrderQueryFilter,
    allShopsFilter,
    datesFilter,
    periodFromFilter,
    periodUntilFilter,
    orderQueryParams,
    setDatesFilter,
    initOrderQueryParams,
    clearOrderFilters,
    updateAssignUserIdFilter,
    selectedFiltersAmount,
    setShopIdsFilter,
  };
});
